import { useEnvironmentInterface } from "environment-interface";
import logger from "features/utils/logger";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";

export const useGlobalPersistedState = <T>(
  key: string,
  initialValue: T,
  version?: number,
): [T, Dispatch<SetStateAction<T>>, () => void] => {
  const storageKey = `CSB/${key}${version ? "/" + version : ""}`;
  return usePersistedState(storageKey, initialValue);
};

export const useScopedPersistedState = <T>(
  key: string,
  initialValue: T,
  scope: string,
  version?: number,
): [T, Dispatch<SetStateAction<T>>, () => void] => {
  const storageKey = `CSB/${key}/${version ? version + "/" : ""}${scope}`;

  return usePersistedState(storageKey, initialValue);
};

/**
 * @deprecated Use one of the new functions:
 *  - useGlobalPersistedState for persisting a single value / user
 *  - useScopedPersistedState for persisting different values / project
 * then remove the export as this remains an internal function
 */
export const usePersistedState = <T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>, () => void] => {
  const { storage } = useEnvironmentInterface();

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item: T | null = storage.get(key);
      return item ?? initialValue;
    } catch (error) {
      logger.error(
        `[persisted-state]: Failed to retrieve data from local storage`,
        error,
      );
      return initialValue;
    }
  });

  useEffect(
    () =>
      storage.subscribe(key, (updatedValue) => {
        if (updatedValue === undefined) {
          setStoredValue(initialValue);
        } else {
          setStoredValue(updatedValue as T);
        }
      }),
    [],
  );

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      setStoredValue((prev) => {
        const valueToStore = value instanceof Function ? value(prev) : value;

        storage.set(key, valueToStore);

        return valueToStore;
      });
    } catch (error) {
      logger.error(`[persisted-state]:`, error);
    }
  };

  const clearValue = () => {
    try {
      setStoredValue(initialValue);
      storage.delete(key);
    } catch (error) {
      logger.error(`[persisted-state]:`, error);
    }
  };
  return [storedValue, setValue, clearValue];
};
